import { apiClient } from '@/shared/lib/apiClient';
import type {
  ApiResponse,
  BookingPricingType,
  BookingQuote,
  Destination,
  GuideAvailability,
  GuideProfile,
  GuideReviewsResponse,
  GuideSchedule,
  PaginatedResponse,
} from '@/types';
import type { GuidesSearchParams } from '@/features/guides/utils/searchParams';

export const guidesApi = {
  async list(params?: GuidesSearchParams) {
    const response = await apiClient.get<PaginatedResponse<GuideProfile>>('/guides', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await apiClient.get<ApiResponse<GuideProfile>>(`/guides/${id}`);
    return response.data.data!;
  },

  async getAvailability(profileId: string, params: { from: string; to: string }) {
    const response = await apiClient.get<ApiResponse<GuideAvailability[]>>(
      `/guides/${profileId}/availability`,
      { params }
    );
    return response.data.data ?? [];
  },

  async getSchedule(profileId: string, params: { from: string; to: string }) {
    const response = await apiClient.get<ApiResponse<GuideSchedule>>(
      `/guides/${profileId}/schedule`,
      { params }
    );
    return response.data.data!;
  },

  async getBookingDestinations(profileId: string) {
    const response = await apiClient.get<ApiResponse<Destination[]>>(
      `/guides/${profileId}/booking-destinations`
    );
    return response.data.data ?? [];
  },

  async getBookingQuote(
    profileId: string,
    params: {
      pricingType: BookingPricingType;
      duration: number;
      travelerCount: number;
    }
  ) {
    const response = await apiClient.get<ApiResponse<BookingQuote>>(
      `/guides/${profileId}/booking-quote`,
      { params }
    );
    return response.data.data!;
  },

  async getReviews(profileId: string, params?: { page?: number; limit?: number }) {
    const response = await apiClient.get<GuideReviewsResponse & { success: boolean }>(
      `/guides/${profileId}/reviews`,
      { params }
    );
    return response.data;
  },
};

export const guidesKeys = {
  all: ['guides'] as const,
  list: (params?: GuidesSearchParams) => [...guidesKeys.all, 'list', params] as const,
  detail: (id: string) => [...guidesKeys.all, 'detail', id] as const,
  availability: (id: string, params: { from: string; to: string }) =>
    [...guidesKeys.all, 'availability', id, params] as const,
  schedule: (id: string, params: { from: string; to: string }) =>
    [...guidesKeys.all, 'schedule', id, params] as const,
  bookingDestinations: (id: string) => [...guidesKeys.all, 'booking-destinations', id] as const,
  quote: (id: string, params: Record<string, unknown>) =>
    [...guidesKeys.all, 'quote', id, params] as const,
  reviews: (id: string, params?: { page?: number; limit?: number }) =>
    [...guidesKeys.all, 'reviews', id, params] as const,
};

/** @deprecated Use guidesApi from '@/features/guides/api/guides.api' */
export const guidesService = guidesApi;
